import { create } from 'zustand';
import type { SessionInfo } from '@vybe/shared';
import { api, ApiError } from '@/services/api';
import { safeStorage } from '@/lib/storage';

const KEY = 'vybe.session';

interface Persisted {
  token: string;
  user: SessionInfo;
}

interface SessionState {
  status: 'checking' | 'anonymous' | 'ready';
  token: string | null;
  user: SessionInfo | null;
  /** Validates a stored token once at startup (the server may have restarted). */
  restore: () => Promise<void>;
  register: (displayName: string) => Promise<void>;
  /** Called when the server rejects our token (e.g. after a restart). */
  invalidate: () => void;
}

function readPersisted(): Persisted | null {
  const raw = safeStorage.get('local', KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Persisted;
    return typeof value.token === 'string' && typeof value.user?.userId === 'string' ? value : null;
  } catch {
    return null;
  }
}

export const useSessionStore = create<SessionState>((set, get) => ({
  status: 'checking',
  token: null,
  user: null,

  restore: async () => {
    const persisted = readPersisted();
    if (!persisted) return set({ status: 'anonymous' });
    try {
      const { session } = await api.me(persisted.token);
      safeStorage.set('local', KEY, JSON.stringify({ token: persisted.token, user: session }));
      set({ status: 'ready', token: persisted.token, user: session });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        safeStorage.remove('local', KEY);
        set({ status: 'anonymous', token: null, user: null });
      } else {
        // Offline/transient: keep the identity; the socket handshake will validate it.
        set({ status: 'ready', token: persisted.token, user: persisted.user });
      }
    }
  },

  register: async (displayName) => {
    const { sessionToken, session } = await api.createSession(displayName);
    safeStorage.set('local', KEY, JSON.stringify({ token: sessionToken, user: session }));
    set({ status: 'ready', token: sessionToken, user: session });
  },

  invalidate: () => {
    if (get().status === 'anonymous') return;
    safeStorage.remove('local', KEY);
    set({ status: 'anonymous', token: null, user: null });
  },
}));
